import axios from 'axios';
import { API_BASE_URL } from '../utils/config';
import { TrainingBalance, MakeupCreditsCheck, TrainingBalanceQueryParams, CoachStatistics, CancelledEvent, UpcomingEvent } from '../types/trainingBalance';

// Helper function to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Service for managing training balance and makeup credits
 */
class TrainingBalanceService {
  /**
   * Get the current user's training balance
   */
  async getMyBalance(params?: TrainingBalanceQueryParams): Promise<TrainingBalance> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.startDate) {
        queryParams.append('startDate', params.startDate.toISOString());
      }
      if (params?.endDate) {
        queryParams.append('endDate', params.endDate.toISOString());
      }
      if (params?.includeBreakdown) {
        queryParams.append('includeBreakdown', 'true');
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/training-balance/my-balance?${queryParams}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      // Silently handle 404 errors
      if (error?.response?.status !== 404) {
        console.error('Error fetching my training balance:', error);
      }
      throw error;
    }
  }

  /**
   * Get a specific user's training balance (Admin/Coach only)
   */
  async getUserBalance(
    userId: string,
    params?: TrainingBalanceQueryParams
  ): Promise<TrainingBalance> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.startDate) {
        queryParams.append('startDate', params.startDate.toISOString());
      }
      if (params?.endDate) {
        queryParams.append('endDate', params.endDate.toISOString());
      }
      if (params?.includeBreakdown) {
        queryParams.append('includeBreakdown', 'true');
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/training-balance/users/${userId}?${queryParams}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      // Silently handle 404 errors
      if (error?.response?.status !== 404) {
        console.error('Error fetching user training balance:', error);
      }
      throw error;
    }
  }

  /**
   * Get training balances for multiple users (Admin/Coach only)
   */
  async getBatchBalances(
    userIds: string[],
    params?: Omit<TrainingBalanceQueryParams, 'includeBreakdown'>
  ): Promise<TrainingBalance[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.startDate) {
        queryParams.append('startDate', params.startDate.toISOString());
      }
      if (params?.endDate) {
        queryParams.append('endDate', params.endDate.toISOString());
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/training-balance/users/batch?${queryParams}`,
        userIds,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      // Silently handle 404 errors
      if (error?.response?.status !== 404) {
        console.error('Error fetching batch training balances:', error);
      }
      throw error;
    }
  }

  /**
   * Check if a user has available makeup credits (Admin/Coach only)
   */
  async checkMakeupCredits(
    userId: string,
    asOfDate?: Date
  ): Promise<MakeupCreditsCheck> {
    try {
      const queryParams = new URLSearchParams();
      if (asOfDate) {
        queryParams.append('asOfDate', asOfDate.toISOString());
      }

      // NEW ENDPOINT: /check-credits/{userId} instead of /users/{userId}/has-credits
      const response = await axios.get(
        `${API_BASE_URL}/api/training-balance/check-credits/${userId}?${queryParams}`,
        { headers: getAuthHeader() }
      );
      console.log('✅ Makeup credits response:', response.status, response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error checking makeup credits:', error?.response?.status, error?.response?.data);
      throw error;
    }
  }

  /**
   * Get coach statistics for the current user (Coach/Admin only)
   */
  async getCoachStatistics(
    startDate: Date,
    endDate: Date,
    includeBreakdown: boolean = false,
    coachId?: string
  ): Promise<CoachStatistics> {
    try {
      const queryParams = new URLSearchParams();
      // HELYI NAP, nem UTC időpillanat [NB-6 osztály, 2026-09-14]: a végpont `DateOnly`-ra
      // csonkolja a kapott DateTime-ot (`TrainingBalanceController.cs:~795`), a `toISOString()`
      // viszont 00:00–02:00 budapesti idő között a TEGNAPI napot adja — ilyenkor a MAI
      // események kiestek a statisztikából. MÉRVE: E2E `flow-quick-wins-extra` 00:45-kor nem
      // találta a ma rögzített órát. A dátumválasztó amúgy is NAP-pontosságú.
      const localDay = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      queryParams.append('startDate', localDay(startDate));
      queryParams.append('endDate', localDay(endDate));
      if (includeBreakdown) {
        queryParams.append('includeBreakdown', 'true');
      }
      if (coachId) {
        queryParams.append('coachId', coachId);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/training-balance/coach-statistics?${queryParams}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching coach statistics:', error);
      throw error;
    }
  }

  /**
   * Get list of cancelled/unregistered events for the current user
   */
  async getMyCancelledEvents(startDate?: Date, endDate?: Date): Promise<CancelledEvent[]> {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) {
        queryParams.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        queryParams.append('endDate', endDate.toISOString());
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/training-balance/my-cancelled-events?${queryParams}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching cancelled events:', error);
      throw error;
    }
  }

  async getMyUpcomingEvents(): Promise<UpcomingEvent[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/training-balance/my-upcoming-events`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching upcoming events:', error);
      throw error;
    }
  }

  /**
   * Calculate period dates for common intervals
   */
  getPeriodDates(interval: 'month' | 'quarter' | 'semester' | 'year'): {
    startDate: Date;
    endDate: Date;
  } {
    const today = new Date();
    const startDate = new Date();
    const endDate = new Date();

    switch (interval) {
      case 'month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'semester':
        startDate.setMonth(today.getMonth() - 6);
        break;
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
    }

    return { startDate, endDate };
  }
}

const trainingBalanceService = new TrainingBalanceService();
export default trainingBalanceService;
export { trainingBalanceService };
